import { useRef, useState, useEffect, useContext } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import Lottie from 'lottie-react'
import { ReactComponent as CampfireSvg } from '../../assets/campfire.svg'
import flameAnimation from '../../assets/flame.json'
import { RouteTransitionContext } from '../../components/AnimatedRoutes'

export default function IntroSection() {
  const sectionRef = useRef<HTMLDivElement>(null)
  const campfireRef = useRef<HTMLDivElement>(null)
  const campfireInnerRef = useRef<HTMLDivElement>(null)
  const creamRef = useRef<HTMLDivElement>(null)
  const [navMargin, setNavMargin] = useState('4%')
  const isTransitioning = useContext(RouteTransitionContext)

  useEffect(() => {
    const calculate = () => {
      const vw = window.innerWidth
      if (vw < 768) {
        setNavMargin('0px')
        return
      }
      const navWidth = Math.min(1105, vw - 32)
      const margin = Math.max(0, (vw - navWidth) / 2)
      setNavMargin(`${margin}px`)
    }
    calculate()
    window.addEventListener('resize', calculate)
    return () => window.removeEventListener('resize', calculate)
  }, [])

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start 0.9', 'end 0.7'],
  })

  const marginX = useTransform(scrollYProgress, [0, 0.6], [navMargin, '0px'])
  const borderRadius = 44

  // Parallax only on desktop — it's visibly jittery on mobile/tablet where
  // scroll-linked transforms don't get their own GPU layer and eat frame budget.
  const isDesktopRef = useRef(typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)')
    const update = () => { isDesktopRef.current = mq.matches }
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])

  const { scrollY } = useScroll()
  const campfireY = useTransform(scrollY, (y) => (isDesktopRef.current ? y * 0.25 : 0))
  const textY = useTransform(scrollY, (y) => (isDesktopRef.current ? y * 0.4 : 0))

  return (
    <div ref={sectionRef} className="relative bg-oak-yellow">
      {/* Campfire container — parallax: scrolls slower than the page */}
      <motion.div
        ref={campfireRef}
        className="pointer-events-none absolute left-1/2 top-[-56vh] z-40 w-[140%] max-w-none md:top-[-400px] md:w-full md:max-w-[800px]"
        style={{
          x: '-50%',
          y: campfireY,
          willChange: 'transform, opacity',
        }}
        animate={{ opacity: isTransitioning ? 0 : 1 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
      >
        <div ref={campfireInnerRef} className="relative">
          <CampfireSvg className="h-auto w-full" />
          <div
            className="absolute"
            style={{
              width: '50%',
              aspectRatio: '1366 / 768',
              left: '50%',
              top: '17.5vh',
              marginLeft: '-23%',
            }}
          >
            <Lottie animationData={flameAnimation} loop autoplay />
          </div>
        </div>
      </motion.div>

      <motion.div
        ref={creamRef}
        data-snake-start
        className="bg-oak-cream px-6 pb-[10vw] pt-12 md:px-6 md:pb-[800px] md:pt-48"
        style={{
          marginLeft: marginX,
          marginRight: marginX,
          borderTopLeftRadius: borderRadius,
          borderTopRightRadius: borderRadius,
        }}
      >
        <div className="mx-auto max-w-nav px-6">
          <motion.div
            className="relative z-50 pb-12 md:pb-20"
            style={{ y: textY, willChange: 'transform' }}
          >
            <div className="max-w-[460px]">
              <p className="text-[19px] font-medium leading-[1.12] tracking-[-0.25px] text-oak-black md:text-[clamp(15px,1.85vw,34px)]">
                The social platform for communities that buy, sell, and trade together.
              </p>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  )
}
